export type ButtonSizeType = "fixed" | "flexible";
export type FixedButtonSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
export type FlexibleButtonSize =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "xxl"
  | "2xl"
  | "3xl";

export const FIXED_DIMENSIONS = {
  xs: {
    width: "3.25rem", // 52px
    height: "1.25rem", // 20px
  },
  sm: {
    width: "3.5625rem", // 57px
    height: "1.125rem", // 18px
  },
  md: {
    width: "8.125rem", // 130px
    height: "1.125rem", // 18px
  },
  lg: {
    width: "8.75rem", // 140px
    height: "1.5625rem", // 25px
  },
  xl: {
    width: "10rem", // 160px
    height: "1.75rem", // 28px
  },
  xxl: {
    width: "14.375rem", // 230px
    height: "2.25rem", // 36px
  },
  "2xl": {
    width: "16.25rem", // 260px
    height: "2.5rem", // 40px
  },
};

export const FLEXIBLE_PADDING = {
  xs: {
    padding: "0.25rem 0.5rem", // 4px 8px
  },
  sm: {
    padding: "0.3125rem 0.75rem", // 6px 12px
  },
  md: {
    padding: "0.3125rem 1rem", // 8px 16px
  },
  lg: {
    padding: "0.3125rem 1.25rem", // 10px 20px
  },
  xl: {
    padding: "0.375rem 1.5rem", // 12px 24px
  },
  xxl: {
    padding: "0.4375rem 2rem", // 16px 32px
  },
  "2xl": {
    padding: "0.4375rem 2.5rem", // 20px 40px
  },
  "3xl": {
    padding: "0.3125rem 3rem", // 24px 48px
  },
};

export const FONT_SIZES = {
  xxs: "0.6125rem", // 10px
  xs: "0.75rem", // 12px
  sm: "0.875rem", // 14px
  md: "1rem", // 16px
  lg: "1.125rem", // 18px
  xl: "1.25rem", // 20px
  xxl: "1.375rem", // 22px
  "2xl": "1.625rem", // 26px
  "3xl": "2rem", // 32px
};

export const BUTTON_TOKENS = {
  // Custom buttons (your original system)
  default: {
    starlight: {
      "--btn-bg": "var(--starlight)",
      "--btn-text": "var(--forest-path)",
    },
    sunbeam: {
      "--btn-bg": "var(--sunbeam)",
      "--btn-text": "var(--forest-path)",
    },
    lupine: {
      "--btn-bg": "var(--lupine)",
      "--btn-text": "var(--forest-path)",
    },
    "wild-hare": {
      "--btn-bg": "var(--wild-hare)",
      "--btn-text": "var(--forest-path)",
    },
    springleaf: {
      "--btn-bg": "var(--springleaf)",
      "--btn-text": "var(--forest-path)",
    },
    "forest-path": {
      "--btn-bg": "var(--forest-path)",
      "--btn-text": "var(--wild-hare)",
    },
    campfire: {
      "--btn-bg": "var(--campfire)",
      "--btn-text": "var(--wild-hare)",
    },
    beautyberry: {
      "--btn-bg": "var(--beautyberry)",
      "--btn-text": "var(--springleaf)",
    }
  },

  modal: {
    primary: {
      "--btn-bg": "var(--starlight)",
      "--btn-text": "var(--forest-path)",
      "--btn-hover-bg": "var(--springleaf)",
      "--btn-hover-text": "var(--forest-path)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--starlight)",
    },
    secondary: {
      "--btn-bg": "var(--glacier)",
      "--btn-text": "var(--forest-path)",
      "--btn-hover-bg": "var(--springleaf)",
      "--btn-hover-text": "var(--forest-path)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--glacier)",
    },
    "confirmation-primary": {
      "--btn-bg": "var(--starlight)",
      "--btn-text": "var(--forest-path)",
      "--btn-hover-bg": "var(--lupine)",
      "--btn-hover-text": "var(--forest-path)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--starlight)",
    },
    "confirmation-secondary": {
      "--btn-bg": "var(--glacier)",
      "--btn-text": "var(--forest-path)",
      "--btn-hover-bg": "var(--lupine)",
      "--btn-hover-text": "var(--forest-path)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--glacier)",
    },
  },

  "no-hover": {
    "1": { "--btn-bg": "#ff6b6b", "--btn-text": "#333333" },
    "2": { "--btn-bg": "#4ecdc4", "--btn-text": "#333333" },
    "3": { "--btn-bg": "#45b7d1", "--btn-text": "#333333" },
    "4": { "--btn-bg": "#f9ca24", "--btn-text": "#333333" },
    glacier: { "--btn-bg": "var(--glacier)", "--btn-text": "var(--wild-hare)" },
  },

  other: {
    primary: {
      "--btn-bg": "var(--starlight)",
      "--btn-text": "var(--forest-path)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--starlight)",
    },
    secondary: {
      "--btn-bg": "var(--forest-path)",
      "--btn-text": "var(--starlight)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--wild-hare)",
    },
    "forest-path": {
      "--btn-bg": "var(--forest-path)",
      "--btn-text": "var(--wild-hare)",
    },
    campfire: {
      "--btn-bg": "var(--campfire)",
      "--btn-text": "var(--wild-hare)",
    },
    riverbed: {
      "--btn-bg": "var(--riverbed)",
      "--btn-text": "var(--wild-hare)",
      "--btn-active-bg": "var(--lupine)",
      "--btn-active-text": "var(--forest-path)",
    },
    glacier: {
      "--btn-bg": "var(--glacier)",
      "--btn-text": "var(--wild-hare)",
      "--btn-active-bg": "var(--glacier)",
      "--btn-active-text": "var(--wild-hare)",
    },
  },
  custom: {
    sunbeam: {
      "--btn-bg": "var(--sunbeam)",
      "--btn-text": "var(--forest-path)",
      "--btn-active-bg": "var(--beautyberry)",
      "--btn-active-text": "var(--wild-hare)",
    },
    lupine: {
      "--btn-bg": "var(--lupine)",
      "--btn-text": "var(--forest-path)",
      "--btn-active-bg": "var(--riverbed)",
      "--btn-active-text": "var(--wild-hare)",
    },
    "forest-path": {
      "--btn-bg": "var(--forest-path)",
      "--btn-text": "var(--wild-hare)",
      "--btn-hover-bg": "var(--beautyberry)",
      "--btn-hover-text": "var(--wild-hare)",
      "--btn-active-bg": "var(--beautyberry)",
      "--btn-active-text": "var(--wild-hare)",
    },
    "forest-path-springleaf": {
      "--btn-bg": "var(--forest-path)",
      "--btn-text": "var(--wild-hare)",
      "--btn-hover-bg": "var(--springleaf)",
      "--btn-hover-text": "var(--forest-path)",
      "--btn-active-bg": "var(--springleaf)",
      "--btn-active-text": "var(--forest-path)",
    },
    glacier: {
      "--btn-bg": "var(--glacier)",
      "--btn-text": "var(--forest-path)",
      "--btn-hover-bg": "var(--forest-path)",
      "--btn-hover-text": "var(--starlight)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--starlight)",
    },
    starlight: {
      "--btn-bg": "var(--starlight)",
      "--btn-text": "var(--forest-path)",
      "--btn-hover-bg": "var(--forest-path)",
      "--btn-hover-text": "var(--starlight)",
      "--btn-active-bg": "var(--forest-path)",
      "--btn-active-text": "var(--starlight)",
      "--disabled-bg": "var(--forest-path-10)",
    },
  },

  circular: {
    primary: {
      "--btn-bg": "var(--springleaf)",
      "--btn-text": "var(--forest-path)",
    },
    secondary: {
      "--btn-bg": "var(--starlight)",
      "--btn-text": "var(--forest-path)",
    },
    delete: {
      "--btn-bg": "var(--campfire)",
      "--btn-text": "var(--forest-path)",
    },
  },
};

export const BUTTON_FEATURES = {
  default: { hasHover: false, hasShadow: true, hasActiveState: true },
  modal: { hasHover: true, hasShadow: false, hasActiveState: true },
  "no-hover": { hasHover: false, hasShadow: true, hasActiveState: false },
  other: { hasHover: false, hasShadow: false, hasActiveState: true },
  custom: { hasHover: true, hasShadow: false, hasActiveState: true },
  circular: { hasHover: false, hasShadow: true, hasActiveState: true },
};

export const CIRCULAR_BUTTON_DIMENSIONS = {
  width: "24px",
  height: "24px",
  aspectRatio: "24/24",
};
